import {
  IDispenseRequest,
  IDispenseRequestFromHandover,
  IRepairOrderRequest,
  ISalesFunding,
  ISalesGroupBy,
  ISalesQuery,
  ISalesStaff,
} from '../@types/dispense';
import {
  CapturePatientSignaturePayload,
  DispenseSignature,
} from '../@types/pcse';
import axios from '../utils/axios';

export class DispenseService {
  getAllDispenses(params?: {
    page?: number;
    limit?: number;
    search?: string;
    orderBy?: string;
    order?: 'ASC' | 'DESC';
    status?: string;
  }) {
    return axios.get('/dispense', { params });
  }
  exportDispenseRecords(query?: string) {
    return axios.get(`/dispense/export/csv${query ? `?${query}` : ''}`, {
      responseType: 'text',
    });
  }
  getDispense(dispenseId: number) {
    return axios.get(`/dispense/${dispenseId}`);
  }
  getDispenseByPatientId(patientId: number | string) {
    return axios.get(`/dispense/patient/${patientId}`);
  }

  getDispensesByClinicalRecord(clinicalRecordId: number) {
    return axios.get(`/dispense/clinical-record/${clinicalRecordId}`);
  }
  createDispense(data: IDispenseRequest) {
    return axios.post('/dispense', data);
  }

  retailTillCheckout(data: Record<string, unknown>) {
    return axios.post('/dispense/retail-till-checkout', data);
  }
  updateDispense(id: number, data: IDispenseRequest) {
    return axios.patch(`/dispense/${id}`, data);
  }
  deleteDispense(id: number) {
    return axios.delete(`/dispense/${id}`);
  }

  // ── Patient signature ─────────────────────────────────────────────────────
  //
  // Captured once, at handover, and reused by any GOS claim raised for this
  // dispense — the patient does not sign twice for the same supply.

  getDispenseSignature(dispenseId: number) {
    return axios.get<DispenseSignature>(`/dispense/${dispenseId}/signature`);
  }

  capturePatientSignature(
    dispenseId: number,
    payload: CapturePatientSignaturePayload,
  ) {
    return axios.post<DispenseSignature>(
      `/dispense/${dispenseId}/signature`,
      payload,
    );
  }

  clearPatientSignature(dispenseId: number) {
    return axios.delete<DispenseSignature>(`/dispense/${dispenseId}/signature`);
  }
  createDispenseFromHandover(
    patientId: string | undefined,
    data: IDispenseRequestFromHandover,
  ) {
    return axios.post(`/dispense/${patientId}`, data);
  }
  createRepairOrder(data: IRepairOrderRequest) {
    return axios.post('/dispense/repair', data);
  }
  getRepairParts() {
    return axios.get('/dispense/repair/parts');
  }
  getAllRepairOrders() {
    return axios.get('/dispense/repair/orders');
  }

  // ── Sales (Dispense is the source of truth for sales activity) ─────────────
  // Bank deposits/fees stay in the Finance API; nothing here reads them.

  /** Headline sales figures for a period: units, value, VAT split, takings. */
  getSalesSummary(params?: ISalesQuery) {
    return axios.get('/dispense/sales/summary', { params });
  }

  /**
   * Item sales aggregated by product, product type, VAT category, month or day
   * — e.g. how many frames were sold in a given month.
   */
  getSalesBreakdown(params?: ISalesQuery & { groupBy?: ISalesGroupBy }) {
    return axios.get('/dispense/sales/breakdown', { params });
  }

  /**
   * NHS versus private split of the period's sales, classified on the NHS
   * voucher band applied to each dispense. Takes the same filters as the other
   * sales endpoints, so the totals reconcile to the sales summary.
   */
  getSalesFunding(params?: ISalesQuery) {
    return axios.get<ISalesFunding>('/dispense/sales/funding', { params });
  }

  /** Sales, spectacles and products dispensed per member of staff. */
  getSalesStaff(params?: ISalesQuery) {
    return axios.get<ISalesStaff>('/dispense/sales/staff', { params });
  }

  /** Line-level item sales with their product-level VAT breakdown. */
  getSalesItems(params?: ISalesQuery & { page?: number; limit?: number }) {
    return axios.get('/dispense/sales/items', { params });
  }

  /** In-app dispense payment ledger (payments and refunds) for a period. */
  getSalesPayments(
    params?: ISalesQuery & { method?: string; page?: number; limit?: number },
  ) {
    return axios.get('/dispense/sales/payments', { params });
  }

  /** Product-level VAT breakdown for a single sale. */
  getSaleVatBreakdown(dispenseId: number) {
    return axios.get(`/dispense/sales/${dispenseId}/vat-breakdown`);
  }
}
